import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Button, ButtonsGroup, Layout, MetricItemAdvance, Text, WidgetDivider } from '@netapp/bxp-design-system-react';
import { SuccessIcon } from '@netapp/bxp-style/react-icons/Notification';
import { history } from 'store';
import { isAllowed, isOrgAdmin } from 'utils/permissions';
import PermissionComponent from 'modules/app/components/permission/PermissionComponent';
import { ConfigurationBaseCard, ConfigurationBaseFooter } from 'modules/console/configurations';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import { ReactComponent as SecurityCertificateIcon } from '../../assets/icon-security-certificate-2color.svg';
import styles from '../../style.module.scss';
// Registers the connectorSettings reducer and the https middleware that owns
// the certificate calls this card reads from.
import 'modules/connector-settings';
import { ViewSignedCertificate } from 'modules/connector-settings/components/https/HttpsModals';

export const SECURITY_CERTIFICATE_ROUTE = '/console-configurations/security-certificate';

const footerStyle = { justifyContent: 'flex-start', borderTop: '0px', alignItems: 'flex-end', marginTop: 'auto' };

const SecurityCertificateCard = () => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const { selectedOrg: { rules } = {} } = useSelector(state => state.tenancy);
    const signedCertificate = useSelector(state => state.connectorSettings?.https?.signedCertificate);
    const certificateLoad = useSelector(state => state.connectorSettings?.https?.certificateLoad);
    const canViewCard = isAllowed(rules, 'notifications', 'access');
    const canConfigure = isOrgAdmin(rules);
    const [viewCertificate, setViewCertificate] = useState(false);

    useEffect(() => {
        if (canViewCard) {
            dispatch({ type: 'CONNECTOR-SETTINGS:FETCH-HTTPS-CERTIFICATE' });
        }
    }, [dispatch, canViewCard]);

    return (
        <ConfigurationBaseCard
            title={t('security_certificate')}
            iconRenderer={() => <SecurityCertificateIcon />}
            isLoading={!!certificateLoad}
            hasNoPermission={!canViewCard}
        >
            <Layout.Grid rowGap='lg'>
                {signedCertificate ? (
                    <>
                        <Layout.GridItem lg={3} className={styles.flexRowAlignEnd}>
                            <MetricItemAdvance
                                className={styles.metricPaddingRight5}
                                label={t('status')}
                                labelTooltip={t('status')}
                                valueSize="small"
                                value={t('installed')}
                                valueTooltip={t('installed')}
                                ValueIcon={SuccessIcon}
                                valueIconColor="success"
                                valueClassName={styles.valueLineHeight24}
                            />
                            <WidgetDivider expand />
                        </Layout.GridItem>
                        <Layout.GridItem lg={5} className={styles.flexRowAlignEndPaddingLeft5}>
                            <MetricItemAdvance
                                className={styles.metricPaddingRight5}
                                label={t('expiration')}
                                labelTooltip={t('expiration')}
                                valueSize="small"
                                value={moment(signedCertificate.endDate).format(t('certificate_date_format'))}
                                valueTooltip={moment(signedCertificate.endDate).format(t('certificate_date_format'))}
                                valueClassName={styles.valueLineHeight24}
                            />
                            <WidgetDivider expand />
                        </Layout.GridItem>
                        <Layout.GridItem lg={4} className={styles.flexRowAlignEndPaddingLeft5}>
                            <MetricItemAdvance
                                className={styles.metricPaddingRight5}
                                label={t('issuer')}
                                labelTooltip={t('issuer')}
                                valueSize="small"
                                value={signedCertificate.directIssuerName || t('n/a')}
                                valueTooltip={signedCertificate.directIssuerName || t('n/a')}
                                valueClassName={styles.valueLineHeight24}
                            />
                        </Layout.GridItem>
                    </>
                ) : (
                    <Layout.GridItem lg={12}>
                        <Text>{t('security_certificate_desc')}</Text>
                    </Layout.GridItem>
                )}
            </Layout.Grid>
            {viewCertificate && (
                <ViewSignedCertificate
                    certificate={signedCertificate}
                    closeTrigger={() => setViewCertificate(false)}
                />
            )}
            <ConfigurationBaseFooter style={footerStyle}>
                <ButtonsGroup>
                    <PermissionComponent hasPermssion={canConfigure}>
                        <Button
                            color="secondary"
                            className={styles.width152}
                            onClick={() => history.push(SECURITY_CERTIFICATE_ROUTE)}
                        >
                            {signedCertificate ? t('edit') : t('configure')}
                        </Button>
                    </PermissionComponent>
                    {signedCertificate && (
                        <Button type="text" onClick={() => setViewCertificate(true)}>
                            {t('view_certificate')}
                        </Button>
                    )}
                </ButtonsGroup>
            </ConfigurationBaseFooter>
        </ConfigurationBaseCard>
    )
}

export default React.memo(SecurityCertificateCard);
